import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ChevronDown, ChevronRight, LogOut, X } from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'
import { cn } from '../../lib/cn'
import {
  designerNavigation,
  filterNavigation,
  fullNavigation,
  isNavigationPathActive,
  type NavNode,
} from '../navigation'
import { hasPermission } from '../access'

const OPEN_GROUPS_KEY = 'pim.sidebar.openGroups.v2'
const NO_PERMISSIONS: string[] = []

interface SidebarProps {
  /** Collapsed mode (desktop): 240px → 64px */
  collapsed: boolean
  /** Mobile drawer open state (only for screens < lg) */
  mobileOpen: boolean
  onCloseMobile: () => void
}

export function Sidebar({ collapsed, mobileOpen, onCloseMobile }: SidebarProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)

  const isDesigner = user?.role === 'designer'
  const permissions = user?.permissions ?? NO_PERMISSIONS

  // Дизайнер видит только свой кабинет; остальные роли — готовые разделы,
  // отфильтрованные по текущей роли.
  const navigation = useMemo(
    () => (
      isDesigner
        ? filterNavigation(designerNavigation, (access) => hasPermission(access, permissions), user?.role)
        : filterNavigation(fullNavigation, (access) => hasPermission(access, permissions), user?.role)
    ),
    [isDesigner, permissions, user?.role],
  )

  const isActive = (fullPath?: string): boolean => isNavigationPathActive(fullPath, location)

  const hasActiveDescendant = (node: NavNode): boolean => {
    if (node.path && isActive(node.path)) return true
    return node.children?.some(hasActiveDescendant) ?? false
  }

  const groupIds = useMemo(() => {
    const ids = new Set<string>()
    const walk = (nodes: NavNode[]) => {
      for (const node of nodes) {
        if (!node.children) continue
        ids.add(node.id)
        walk(node.children)
      }
    }
    walk(navigation)
    return ids
  }, [navigation])

  const activeGroupIds = useMemo(() => {
    const ids: string[] = []
    const walk = (nodes: NavNode[]) => {
      for (const node of nodes) {
        if (!node.children) continue
        const hasActive = (candidate: NavNode): boolean =>
          (candidate.path && isNavigationPathActive(candidate.path, location)) ||
          candidate.children?.some(hasActive) ||
          false
        if (hasActive(node)) ids.push(node.id)
        walk(node.children)
      }
    }
    walk(navigation)
    return ids
  }, [location.pathname, location.search, navigation])

  // Restore only ids which exist in the current role's navigation. Active
  // groups are added so a deep link never lands inside a hidden branch.
  const [openGroups, setOpenGroups] = useState<string[]>(() => {
    const open = new Set<string>()
    try {
      const stored = JSON.parse(localStorage.getItem(OPEN_GROUPS_KEY) || '[]')
      if (Array.isArray(stored)) {
        for (const id of stored) {
          if (typeof id === 'string' && groupIds.has(id)) open.add(id)
        }
      }
    } catch {}
    for (const id of activeGroupIds) open.add(id)
    if (isDesigner) open.add('designer-cabinet')
    return [...open]
  })

  useEffect(() => {
    setOpenGroups((current) => {
      const next = new Set(current.filter((id) => groupIds.has(id)))
      for (const id of activeGroupIds) next.add(id)
      if (isDesigner) next.add('designer-cabinet')
      const value = [...next]
      return value.length === current.length && value.every((id, index) => id === current[index])
        ? current
        : value
    })
  }, [activeGroupIds, groupIds, isDesigner])

  useEffect(() => {
    try {
      localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(openGroups))
    } catch {}
  }, [openGroups])

  const toggleGroup = (id: string) => {
    setOpenGroups((prev) => (prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]))
  }

  const handleNavigate = (path: string) => {
    navigate(path)
    onCloseMobile()
  }

  const renderNode = (node: NavNode, depth: number): React.ReactNode => {
    const Icon = node.icon

    // ── Group (dropdown) ──
    if (node.children) {
      const isOpen = openGroups.includes(node.id)
      const activeInside = hasActiveDescendant(node)
      return (
        <div key={node.id} className="mb-0.5">
          <button
            data-tour={`nav-${node.id}`}
            onClick={() => toggleGroup(node.id)}
            title={collapsed ? node.label : undefined}
            className={cn(
              'w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md',
              'text-sm font-medium transition-colors',
              activeInside && !isOpen
                ? 'text-indigo-700 dark:text-indigo-300'
                : 'text-gray-700 dark:text-zinc-300',
              'hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-zinc-800 dark:hover:text-zinc-100',
              depth === 0 && collapsed && 'lg:justify-center lg:px-2',
            )}
          >
            <Icon
              className={cn(
                depth === 0 ? 'h-5 w-5' : 'h-4 w-4',
                'shrink-0',
                activeInside && !isOpen
                  ? 'text-indigo-600 dark:text-indigo-400'
                  : 'text-gray-500 dark:text-zinc-500',
              )}
            />
            <span
              className={cn('flex-1 text-left truncate', depth === 0 && collapsed && 'lg:hidden')}
            >
              {node.label}
            </span>
            {!(depth === 0 && collapsed) &&
              (isOpen ? (
                <ChevronDown className="h-3.5 w-3.5 text-gray-400 shrink-0" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 text-gray-400 shrink-0" />
              ))}
          </button>

          {isOpen && !(depth === 0 && collapsed) && (
            <div
              className={cn(
                'mt-0.5 border-l border-gray-100 dark:border-zinc-800 space-y-0.5',
                depth === 0 ? 'ml-4 pl-2' : 'ml-2.5 pl-1',
              )}
            >
              {node.children.map((child) => renderNode(child, depth + 1))}
            </div>
          )}
        </div>
      )
    }

    // ── Leaf (link) ──
    const active = isActive(node.path)

    // Top-level link (Главная, Поиск)
    if (depth === 0) {
      return (
        <button
          key={node.id}
          data-tour={`nav-${node.id}`}
          onClick={() => handleNavigate(node.path || '/')}
          title={collapsed ? node.label : undefined}
          className={cn(
            'w-full flex items-center gap-2.5 py-1.5 rounded-md mb-0.5',
            'text-sm font-medium transition-colors relative',
            active
              ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300 pl-[calc(0.625rem-3px)] pr-2.5 before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[3px] before:rounded-r before:bg-indigo-600'
              : 'text-gray-700 px-2.5 hover:bg-gray-100 hover:text-gray-900 dark:text-zinc-300 dark:hover:bg-zinc-800 dark:hover:text-zinc-100',
            collapsed && 'lg:justify-center lg:!px-2',
          )}
        >
          <Icon
            className={cn(
              'h-5 w-5 shrink-0',
              active ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-500 dark:text-zinc-500',
            )}
          />
          <span className={cn('flex-1 text-left truncate', collapsed && 'lg:hidden')}>
            {node.label}
          </span>
        </button>
      )
    }

    // Nested link
    return (
      <button
        key={node.id}
        data-tour={`nav-${node.id}`}
        onClick={() => handleNavigate(node.path || '/')}
        className={cn(
          'w-full flex items-center gap-2.5 py-1.5 pr-2.5 rounded-md',
          'text-sm font-medium transition-colors relative',
          active
            ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300 pl-[calc(0.625rem-3px)] before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[3px] before:rounded-r before:bg-indigo-600'
            : 'text-gray-600 pl-2.5 hover:bg-gray-50 hover:text-gray-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-100',
        )}
      >
        <Icon
          className={cn(
            'h-4 w-4 shrink-0',
            active ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400 dark:text-zinc-500',
          )}
        />
        <span className="truncate text-left">{node.label}</span>
      </button>
    )
  }

  const initial = user?.user_name?.charAt(0).toUpperCase() || '?'

  return (
    <>
      {/* Mobile backdrop */}
      <div
        className={cn(
          'lg:hidden fixed inset-0 z-30 bg-black/40 transition-opacity',
          mobileOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
        onClick={onCloseMobile}
        aria-hidden="true"
      />

      <aside
        className={cn(
          'group/sidebar flex flex-col bg-white dark:bg-zinc-900 border-r border-gray-200 dark:border-zinc-800 shrink-0',
          'transition-[width,transform] duration-base ease-snap',
          'fixed inset-y-0 left-0 z-40 lg:relative lg:translate-x-0',
          collapsed ? 'lg:w-[64px]' : 'lg:w-[240px]',
          'w-[240px]',
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
      >
        {/* Brand row — 56px */}
        <div className="h-14 px-3 flex items-center gap-2.5 border-b border-gray-200 dark:border-zinc-800 shrink-0">
          <div className="h-7 w-7 bg-indigo-600 rounded-md flex items-center justify-center shrink-0">
            <span className="text-white text-sm font-bold leading-none">P</span>
          </div>
          <span
            className={cn(
              'text-base font-bold text-gray-900 dark:text-zinc-100 tracking-tight whitespace-nowrap overflow-hidden',
              collapsed && 'lg:hidden',
            )}
          >
            PIM
          </span>
          <button
            className="ml-auto lg:hidden text-gray-400 hover:text-gray-600 dark:text-zinc-500 dark:hover:text-zinc-300 p-1"
            onClick={onCloseMobile}
            aria-label="Закрыть меню"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation — scrollable */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-3">
          {navigation.map((node) => renderNode(node, 0))}
        </nav>

        {/* Logout */}
        <div className="px-2 py-2 border-t border-gray-200 dark:border-zinc-800 shrink-0">
          <button
            onClick={() => {
              logout()
              navigate('/login')
            }}
            title={collapsed ? 'Выйти' : undefined}
            className={cn(
              'w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md',
              'text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-zinc-300 dark:hover:bg-zinc-800 dark:hover:text-zinc-100 transition-colors',
              collapsed && 'lg:justify-center lg:px-2',
            )}
          >
            <LogOut className="h-5 w-5 shrink-0 text-gray-500 dark:text-zinc-500" />
            <span className={cn('flex-1 text-left truncate', collapsed && 'lg:hidden')}>Выйти</span>
          </button>
        </div>

        {/* User row — pinned bottom */}
        <div
          className={cn(
            'px-3 py-3 border-t border-gray-200 dark:border-zinc-800 flex items-center gap-2.5 shrink-0',
            collapsed && 'lg:justify-center',
          )}
        >
          <div className="h-8 w-8 rounded-full shrink-0 bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white text-xs font-semibold">
            {initial}
          </div>
          <div className={cn('flex-1 min-w-0', collapsed && 'lg:hidden')}>
            <p className="text-xs font-semibold text-gray-900 dark:text-zinc-100 truncate">
              {user?.user_name || 'Гость'}
            </p>
            <p className="text-2xs text-gray-500 dark:text-zinc-400 truncate">
              {user?.email || ''}
            </p>
          </div>
        </div>
      </aside>
    </>
  )
}
